import { FC, useCallback } from 'react';
import { Jane } from 'types/graphql';
import { BKJAvatar, BKJCheckboxListItem } from '@bookjane2/bookjane-design-library';
import {
  AvatarIconWrapper,
  JaneSelect,
} from 'components/SearchTeamMembers/SearchTeamMembers.styled';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import {
  isMultiLocationCallOutFeatureEnabledSelector,
  isMultiLocationTieringFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { StyledBKJIcon } from 'components/JaneGridSelect/JaneGridSelect.styled';
import { EventTarget } from 'types/common.types';
import { Flex } from 'components/Flex';

interface ITeamMemberOption {
  node: Jane;
  value: string;
  name: string;
  onSelect: (e: unknown) => void;
}

const CustomJaneSelect = styled(JaneSelect)`
  cursor: pointer;

  > svg {
    top: 3px;
    left: 13px;
  }
`;

export const TeamMemberOption: FC<ITeamMemberOption> = ({ node, value, name, onSelect }) => {
  const fullName = `${node?.firstName} ${node?.lastName}`;
  const currentlySelectedLocation = getCurrentlySelectedLocation();

  const isMultiLocationCallOutFeatureEnabled = useSelector(
    isMultiLocationCallOutFeatureEnabledSelector(Number(currentlySelectedLocation?.value)),
  );
  const isMultiLocationTieringFeatureEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(currentlySelectedLocation?.value)),
  );
  const isSharedJanePrimaryCommunityDifferentFromSelectedLocation =
    (!!isMultiLocationCallOutFeatureEnabled || !!isMultiLocationTieringFeatureEnabled) &&
    !node?.agencyCommunity &&
    String(node?.primaryCommunity?.id) !== String(currentlySelectedLocation?.value);

  const handleSelect = useCallback(
    (event: EventTarget) => {
      onSelect({
        ...event,
        target: {
          ...event.target,
          value: fullName,
          label: fullName,
          janeId: node.id,
        },
      });
    },
    [fullName, node.id, onSelect],
  );

  const handleCheckboxChange = (event: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof event === 'object' && event && 'stopPropagation' in (event as any)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (event as any).stopPropagation?.();
    }
    handleSelect(event as EventTarget);
  };

  return (
    <CustomJaneSelect
      key={node.id}
      alignItems="center"
      selected={value === fullName}
      onClick={handleSelect}
    >
      <AvatarIconWrapper>
        <BKJAvatar
          src={node.avatarUrls.small}
          width="30px"
          height="30px"
          fallbackAvatarColor="Grey4"
        />
        {node?.pendingSignup && (
          <StyledBKJIcon iconName="PendingSignup" left="17" top="16" width="20" />
        )}
      </AvatarIconWrapper>
      <Flex flexDirection="row" alignItems="center">
        <BKJCheckboxListItem
          checkboxVariant="GreenNoBorder"
          listItemVariant="RowReverseSpaceBetween"
          showPeopleIcon={isSharedJanePrimaryCommunityDifferentFromSelectedLocation}
          value={fullName}
          label={fullName}
          name={name}
          selected={[value]}
          onChange={handleCheckboxChange}
          css="padding-right: 0; width: 100%; gap: 0;"
        />
      </Flex>
    </CustomJaneSelect>
  );
};
